import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, ReceiptText, Timer, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { StatCard } from "@/components/common/StatCard";
import { EmptyState } from "@/components/common/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { salesApi } from "@/lib/api";
import { formatMoney, formatNumber, localDateISO } from "@/lib/utils";

/** Today's sales for one restaurant: headline cards, by-hour chart, top items. */
export function SalesTab({ tenantId }: { tenantId: number }) {
  const today = localDateISO();

  const sales = useQuery({
    queryKey: ["sales", "today", tenantId],
    queryFn: () => salesApi.today(tenantId),
  });

  const topItems = useQuery({
    queryKey: ["sales", "top-items", tenantId, today],
    queryFn: () => salesApi.topItems(tenantId, today, today),
  });

  const hourly = useMemo(() => {
    const byHour = sales.data?.byHour ?? [];
    if (byHour.length === 0) return [];
    const totals = new Map(byHour.map((h) => [h.hour, h.total]));
    const hours = byHour.map((h) => h.hour);
    const min = Math.min(...hours);
    const max = Math.max(...hours);
    const rows: { label: string; total: number }[] = [];
    for (let h = min; h <= max; h++) {
      rows.push({ label: `${String(h).padStart(2, "0")}:00`, total: totals.get(h) ?? 0 });
    }
    return rows;
  }, [sales.data]);

  const maxTopTotal = Math.max(1, ...(topItems.data ?? []).map((t) => t.total));

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Today's gross"
          value={sales.data ? formatMoney(sales.data.todayGross) : "…"}
          icon={Banknote}
          tone="success"
        />
        <StatCard
          label="Orders"
          value={sales.data ? formatNumber(sales.data.orderCount) : "…"}
          icon={ReceiptText}
        />
        <StatCard
          label="Avg order"
          value={sales.data ? formatMoney(sales.data.avgOrder) : "…"}
          icon={TrendingUp}
        />
        <StatCard
          label="Open orders"
          value={sales.data ? formatNumber(sales.data.openOrders) : "…"}
          icon={Timer}
          tone="warning"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Sales by hour</CardTitle>
            <CardDescription>Gross takings per hour today (TZS)</CardDescription>
          </CardHeader>
          <CardContent>
            {sales.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : hourly.length === 0 ? (
              <EmptyState
                icon={TrendingUp}
                title="No sales yet today"
                description="The chart fills in as payments are recorded."
              />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hourly} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="label" stroke="var(--color-muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--color-muted)", opacity: 0.6 }}
                    formatter={(value) => [formatMoney(Number(value)), "Gross"]}
                    contentStyle={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      color: "var(--color-foreground)",
                    }}
                  />
                  <Bar dataKey="total" fill="var(--color-primary)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top items today</CardTitle>
            <CardDescription>By gross takings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {topItems.isLoading && <Skeleton className="h-40 w-full" />}
            {topItems.data && topItems.data.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">Nothing sold yet.</p>
            )}
            {(topItems.data ?? []).slice(0, 8).map((item) => (
              <div key={item.itemName} className="relative overflow-hidden rounded-[var(--radius-sm)]">
                <div
                  className="absolute inset-y-0 left-0 bg-primary/10"
                  style={{ width: `${Math.max(4, (item.total / maxTopTotal) * 100)}%` }}
                />
                <div className="relative flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
                  <span className="min-w-0 flex-1 truncate font-medium">{item.itemName}</span>
                  <span className="text-xs text-muted-foreground">{formatNumber(item.qty)}×</span>
                  <span className="font-semibold tabular-nums">{formatMoney(item.total)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
